export const CONFIDENCE_CALIBRATION_CONTRACT_ID = "confidence-calibration-v1-sr33" as const;

export type ConfidenceFeatureName =
  | "ocr_confidence"
  | "pass_agreement"
  | "grammar_plausibility"
  | "geometry_confidence"
  | "template_quality"
  | "sharpness_quality"
  | "contrast_quality"
  | "reflection_quality"
  | "clipping_quality"
  | "non_planarity_quality"
  | "variant_stability"
  | "cnn_confidence"
  | "device_runtime_stability";

export type ConfidenceRuntimeFeatures = Partial<Record<ConfidenceFeatureName, number>>;

export interface ConfidenceCalibrationArtifact {
  schema_version: 1;
  contract_id: typeof CONFIDENCE_CALIBRATION_CONTRACT_ID;
  artifact_sha256: string;
  feature_contract: {
    allowed: ConfidenceFeatureName[];
    required_groups?: Array<"ocr_or_nn" | "geometry_or_quality" | "agreement_or_grammar">;
    forbidden_name_fragments: string[];
    feature_weights: Partial<Record<ConfidenceFeatureName, number>>;
  };
  models: Record<string, {
    status: string;
    model?: { points?: Array<{ max_score: number; probability: number }> };
    threshold?: { policy?: string; threshold?: number | null };
  }>;
  threshold_policy: "BENCHMARK_DERIVED";
  production_activation_allowed: false;
}

export interface CalibratedConfidenceValue {
  probability: number | null;
  threshold: number | null;
  uncertain: boolean;
  evidenceScore: number | null;
  featuresUsed: string[];
}

const CONFIDENCE_FEATURE_GROUPS: Record<string, ReadonlySet<ConfidenceFeatureName>> = {
  ocr_or_nn: new Set(["ocr_confidence", "cnn_confidence"]),
  geometry_or_quality: new Set(["geometry_confidence", "template_quality", "sharpness_quality", "contrast_quality", "reflection_quality", "clipping_quality", "non_planarity_quality", "device_runtime_stability"]),
  agreement_or_grammar: new Set(["pass_agreement", "grammar_plausibility", "variant_stability"]),
};

const clamp01 = (value: unknown): number | null => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.min(1, number)) : null;
};

function assertRuntimeFeatures(artifact: ConfidenceCalibrationArtifact, features: Record<string, unknown>) {
  const allowed = new Set<string>(artifact.feature_contract.allowed ?? []);
  const forbidden = (artifact.feature_contract.forbidden_name_fragments ?? []).map((value) => value.toLowerCase());
  for (const key of Object.keys(features)) {
    const lowered = key.toLowerCase();
    if (forbidden.some((token) => lowered.includes(token))) throw new Error(`Forbidden confidence feature: ${key}`);
    if (!allowed.has(key)) throw new Error(`Unknown confidence feature: ${key}`);
  }
}

export function confidenceEvidenceScore(artifact: ConfidenceCalibrationArtifact, features: ConfidenceRuntimeFeatures): { score: number | null; featuresUsed: ConfidenceFeatureName[] } {
  assertRuntimeFeatures(artifact, features as Record<string, unknown>);
  let weighted = 0;
  let total = 0;
  const featuresUsed: ConfidenceFeatureName[] = [];
  for (const key of Object.keys(features).sort() as ConfidenceFeatureName[]) {
    const value = clamp01(features[key]);
    if (value == null) continue;
    const weight = Math.max(0, Number(artifact.feature_contract.feature_weights?.[key] ?? 1));
    if (!weight) continue;
    weighted += value * weight;
    total += weight;
    featuresUsed.push(key);
  }
  const used = new Set<ConfidenceFeatureName>(featuresUsed);
  const missingRequiredGroup = (artifact.feature_contract.required_groups ?? []).some((group) => {
    const members = CONFIDENCE_FEATURE_GROUPS[group];
    return !members || !Array.from(members).some((feature) => used.has(feature));
  });
  return { score: total > 0 && !missingRequiredGroup ? weighted / total : null, featuresUsed };
}

function predict(model: ConfidenceCalibrationArtifact["models"][string] | undefined, score: number | null): number | null {
  if (score == null || model?.status !== "calibrated") return null;
  const points = model.model?.points ?? [];
  if (!points.length) return null;
  const point = points.find((entry) => score <= Number(entry.max_score) + 1e-15) ?? points[points.length - 1];
  return clamp01(point?.probability);
}

function calibrateOne(artifact: ConfidenceCalibrationArtifact, target: string, features: ConfidenceRuntimeFeatures): CalibratedConfidenceValue {
  if (artifact.contract_id !== CONFIDENCE_CALIBRATION_CONTRACT_ID) throw new Error("Unsupported Confidence Calibration contract");
  if (artifact.threshold_policy !== "BENCHMARK_DERIVED") throw new Error("Confidence threshold is not benchmark-derived");
  if (artifact.production_activation_allowed !== false) throw new Error("Research confidence artifact may not auto-activate Production");
  const evidence = confidenceEvidenceScore(artifact, features);
  const model = artifact.models[target];
  const probability = predict(model, evidence.score);
  const threshold = clamp01(model?.threshold?.threshold);
  return { probability, threshold, uncertain: probability == null || threshold == null || probability < threshold, evidenceScore: evidence.score, featuresUsed: evidence.featuresUsed };
}

export function applyConfidenceCalibration(
  artifact: ConfidenceCalibrationArtifact,
  fieldFeatures: Record<"materialText" | "materialType" | "materialNumber" | "storageAddress", ConfidenceRuntimeFeatures>,
  globalFeatures: ConfidenceRuntimeFeatures,
) {
  return {
    contractId: artifact.contract_id,
    artifactSha256: artifact.artifact_sha256,
    fields: {
      materialText: calibrateOne(artifact, "materialText", fieldFeatures.materialText),
      materialType: calibrateOne(artifact, "materialType", fieldFeatures.materialType),
      materialNumber: calibrateOne(artifact, "materialNumber", fieldFeatures.materialNumber),
      storageAddress: calibrateOne(artifact, "storageAddress", fieldFeatures.storageAddress),
    },
    fourOfFour: calibrateOne(artifact, "four_of_four_exact", globalFeatures),
  };
}
